/**
 * In-App Purchase Service for Capacitor with RevenueCat
 *
 * SUBSCRIPTION MODEL (no free trial):
 * - Monthly: tanzeen_pro_monthly (base plan monthly-base)
 * - Yearly:  tanzeen_pro_yearly  (base plan yearly-base)
 * - Lifetime: tanzeen_pro_lifetime (non-consumable, one-time)
 *
 * NOTE: Neither `monthly-base` nor `yearly-base` currently has a free-trial
 * offer configured in Google Play Console. The app therefore MUST NOT advertise
 * or simulate a free trial. If a Free-trial offer is later added in the Play
 * Console, RevenueCat will surface it via `entitlement.periodType === 'trial'`
 * and CustomerInfo will reflect it automatically — no code change needed here.
 *
 * WEB MODE: Uses localStorage mock for UI testing (no purchases).
 * NATIVE MODE: Uses RevenueCat SDK for real Play Store IAP.
 *
 * SETUP FOR NATIVE BUILDS:
 * 1. Install RevenueCat: yarn add @revenuecat/purchases-capacitor
 * 2. Create products in Play Console (already done, verified)
 * 3. Configure products and offerings in RevenueCat dashboard (already done)
 * 4. Set REACT_APP_REVENUECAT_API_KEY and REACT_APP_REVENUECAT_ENTITLEMENT_ID
 * 5. Run: npx cap sync
 */

import { Capacitor } from '@capacitor/core';

// RevenueCat entitlement id. Must match EXACTLY what's set as the entitlement
// identifier in the RevenueCat dashboard. Production dashboard uses "Pro"
// (capital P). If this string doesn't match, customerInfo.entitlements.active
// returns undefined and paying users stay locked out.
//
// The production entitlement is "Pro". The default below is set to "Pro" so
// the app remains correct even if REACT_APP_REVENUECAT_ENTITLEMENT_ID isn't
// present at build time. There is a separate "Tanzeen + Pro" entitlement in
// RevenueCat marked TEST ONLY — DO NOT check for it.
const ENTITLEMENT_ID = process.env.REACT_APP_REVENUECAT_ENTITLEMENT_ID || 'Pro';

// Product IDs - must match App Store Connect & Google Play Console
// Configure these in .env or they default to standard naming convention
export const PRODUCT_IDS = {
  PRO_MONTHLY: process.env.REACT_APP_PRODUCT_ID_MONTHLY || 'tanzeen_pro_monthly',
  PRO_YEARLY: process.env.REACT_APP_PRODUCT_ID_YEARLY || 'tanzeen_pro_yearly',
  PRO_LIFETIME: process.env.REACT_APP_PRODUCT_ID_LIFETIME || 'tanzeen_pro_lifetime',
};

// NOTE (2026-02): The old hard-coded SUBSCRIPTION_PRICES export lived here
// with strings like `$2.99/month`, `$19.99/year`, `$49.99 once`. That was the
// root cause of the paywall showing stale prices when RevenueCat had already
// been updated in the Google Play Console. It has been removed on purpose.
// The ONLY source of truth for live pricing is
// `pkg.product.priceString` on each RevenueCat offering package (localised,
// currency-correct, always in sync with the store). If you need to display
// a price anywhere in the app, read it from a live offering — never invent
// one here.

// Purchase result statuses
export const PURCHASE_STATUS = {
  SUCCESS: 'success',
  CANCELLED: 'cancelled',
  ERROR: 'error',
  PENDING: 'pending',
  RESTORED: 'restored',
};

// Check if running in native Capacitor environment
export const isNativeApp = () => {
  try {
    return Capacitor.isNativePlatform();
  } catch {
    return false;
  }
};

// Get platform
export const getPlatform = () => {
  try {
    return Capacitor.getPlatform();
  } catch {
    return 'web';
  }
};

/**
 * PurchaseService - Singleton for managing in-app purchases with RevenueCat
 * 
 * Web: Uses localStorage mock for UI testing
 * Native: Uses RevenueCat for real subscriptions with App Store/Play Store
 */
// Storage keys owned by this service. Kept as constants so the ProContext
// (and tests) can invalidate them by name during account switches.
export const LOCAL_SUB_KEY = 'quranplus_subscription';
export const LOCAL_SUB_BOUND_USER_KEY = 'quranplus_subscription_bound_user';
export const VERIFICATION_CACHE_KEY = 'tanzeel_entitlement_cache';

class PurchaseService {
  constructor() {
    this.initialized = false;
    this.products = [];
    this.offerings = null;
    this.revenueCat = null;
    this.customerInfo = null;
    // Shared Promise for an in-flight restorePurchases() call. Any concurrent
    // caller (double-tap on the Restore button, DuasPage 403 retry firing
    // while the user also tapped Restore) awaits the same promise and gets
    // the same result — no parallel Purchases.restorePurchases() calls to
    // the Play Store, no duplicate backend syncs, no racing UI toasts.
    this._restoreInFlight = null;
  }

  /**
   * Initialize the purchase service
   * Call this on app startup.
   *
   * @param {string|null} userId - Stable app-level user id (from
   *   ProContext.getUserId — the persistent localStorage-backed installation
   *   id used by /api/entitlements/verify/{userId} and Stripe checkout). We
   *   forward it as `appUserID` to RevenueCat.configure so purchases are
   *   attached to the same identity the rest of the app uses — this is what
   *   was previously undefined-anonymous and caused paying subscribers to
   *   appear locked out on native builds.
   *
   *   Passing the SAME userId on subsequent calls is idempotent (RevenueCat
   *   returns `created: false`). Passing a different userId later would
   *   re-bind the SDK to that account; the app has no auth UI so this only
   *   happens when the underlying quranplus_user_id changes (fresh install,
   *   user cleared data, dev-only pro_user URL param override).
   */
  async initialize(userId = null) {
    // If caller didn't pass a userId, resolve from localStorage so late
    // call-sites (e.g. paywall re-mount) still bind to the app's stable
    // identity rather than an anonymous RevenueCat id.
    if (!userId) {
      try {
        userId = window?.localStorage?.getItem('quranplus_user_id') || null;
      } catch (_) { /* localStorage may throw in Safari private mode */ }
    }

    // Account-switch guard. If the id we're about to bind is different from
    // the id we previously cached local Pro state against, purge every
    // client-side cache that could otherwise leak User A's Pro entitlement
    // into User B's session (fresh install w/ shared device, cleared data,
    // dev `?pro_user=` override, uninstall+reinstall). Server-side
    // `verify_entitlement` will re-authoritatively decide is_pro for the
    // new id on the next call.
    try {
      const boundUser = window?.localStorage?.getItem(LOCAL_SUB_BOUND_USER_KEY);
      if (userId && boundUser && boundUser !== userId) {
        console.log('[IAP] appUserId changed — purging local Pro caches. was=', boundUser, 'now=', userId);
        try { window.localStorage.removeItem(LOCAL_SUB_KEY); } catch (_) {}
        try { window.localStorage.removeItem(VERIFICATION_CACHE_KEY); } catch (_) {}
      }
      if (userId) {
        try { window.localStorage.setItem(LOCAL_SUB_BOUND_USER_KEY, userId); } catch (_) {}
      }
    } catch (_) {}

    // Guard against double-init (React StrictMode remount, resume-on-foreground
    // path). If the caller passed a different userId than the one already
    // bound, we still fall through to re-log-in below.
    if (this.initialized && this._appUserId === userId) return;

    try {
      if (isNativeApp()) {
        try {
          const { Purchases, LOG_LEVEL } = await import('@revenuecat/purchases-capacitor');
          this.revenueCat = Purchases;
          
          // Enable debug logging in development
          if (process.env.NODE_ENV === 'development') {
            await this.revenueCat.setLogLevel({ level: LOG_LEVEL.DEBUG });
          }
          
          // Configure RevenueCat with platform-specific API key
          // SECURITY: No fallback key - must be explicitly configured
          const platform = getPlatform();
          const apiKey = process.env.REACT_APP_REVENUECAT_API_KEY;
          
          if (!apiKey) {
            console.warn('[IAP] REACT_APP_REVENUECAT_API_KEY not configured - purchases disabled');
            throw new Error('RevenueCat API key not configured');
          }
          
          // Configure with the app's stable user id so entitlements are
          // bound to the same identity used by the server-side check
          // (`/api/entitlements/verify/{userId}`) and by Stripe checkout.
          // If userId is null we let RevenueCat generate an anonymous id,
          // but ProContext always provides one in practice.
          await this.revenueCat.configure({
            apiKey,
            appUserID: userId || undefined,
          });
          this._appUserId = userId || null;

          // If the SDK was previously configured with a different id, force
          // a logIn so the current stream is bound to the intended user.
          // logIn is idempotent for the same id.
          if (userId) {
            try {
              const { customerInfo: loggedInInfo, created } =
                await this.revenueCat.logIn({ appUserID: userId });
              this.customerInfo = loggedInInfo;
              this._appUserId = userId;
              if (process.env.NODE_ENV === 'development') {
                console.log(`[IAP] logIn ok — appUserID="${userId}" created=${created}`);
              }
            } catch (loginErr) {
              // Non-fatal: configure() already set appUserID; logIn is a
              // belt-and-braces re-bind for cases where the app reboots into
              // a different id (e.g. cleared data).
              console.warn('[IAP] logIn after configure failed:', loginErr?.message);
            }
          }
          
          // Fetch current customer info
          this.customerInfo = await this.revenueCat.getCustomerInfo();

          // Push the current entitlement snapshot to the backend on every
          // app open so server-side gates (Tajweed tokens, Du'a Generator,
          // Halal Scanner...) see the user as Pro without waiting for a
          // Restore Purchases tap or a webhook.
          try {
            const hasPro = this.hasActiveEntitlement(this.customerInfo);
            this.syncEntitlementToBackend(this.customerInfo, hasPro).catch(() => {});
          } catch (_) {}
          
          // Fetch offerings (products configured in RevenueCat dashboard)
          this.offerings = await this.revenueCat.getOfferings();
          
          if (this.offerings.current?.availablePackages) {
            this.products = this.offerings.current.availablePackages.map(pkg => ({
              productId: pkg.product.identifier,
              title: pkg.product.title,
              description: pkg.product.description,
              price: pkg.product.priceString,
              priceAmount: pkg.product.price,
              currencyCode: pkg.product.currencyCode,
              subscriptionPeriod: pkg.product.subscriptionPeriod,
              introPrice: pkg.product.introPrice, // Genuine store intro-offer info (may be null). NOT used to advertise a trial anywhere in the app — only surfaced to callers that want to reflect what the store itself reports.
              package: pkg,
            }));
          }
          
          // Set up listener for subscription changes (cross-device sync, webhook,
          // and delayed Google Play propagation). We fan out to any external
          // observers so ProContext can flip isPro→true immediately.
          await this.revenueCat.addCustomerInfoUpdateListener((customerInfo) => {
            this.customerInfo = customerInfo;
            const hasPro = this.hasActiveEntitlement(customerInfo);
            console.log('[IAP] Customer info updated, hasPro=', hasPro);
            // Push the SDK-verified entitlement to the backend so future
            // server-side feature checks see the user as Pro even when the
            // backend has no direct RevenueCat API key.
            this.syncEntitlementToBackend(customerInfo, hasPro).catch(() => {});
            (this._externalListeners || []).forEach(cb => {
              try { cb(customerInfo, hasPro); } catch (e) {
                console.warn('[IAP] External listener error:', e?.message);
              }
            });
          });
          
          console.log('[IAP] RevenueCat initialized successfully');
        } catch (e) {
          console.log('[IAP] RevenueCat not available, using web mock:', e.message);
          this.revenueCat = null;
        }
      }
      
      // If RevenueCat did not return any packages (native init failed, or
      // running in a non-native context), leave `this.products` empty. The
      // paywall renders a friendly loading/error state instead of inventing
      // prices. NEVER re-introduce a hard-coded fallback catalogue here —
      // that produced stale $2.99 / $19.99 / $49.99 in production.
      
      this.initialized = true;
    } catch (error) {
      console.error('[IAP] Initialization error:', error);
      this.initialized = true; // Mark as initialized to prevent infinite loops
    }
  }

  /**
   * Get all available products
   */
  getProducts() {
    return this.products;
  }

  /**
   * Push the RevenueCat-verified customer entitlement to our backend so
   * server-side feature checks can grant access even when the backend
   * has no direct RevenueCat API key configured. Fire-and-forget — never
   * throws to the caller. Idempotent on the backend (upsert).
   */
  async syncEntitlementToBackend(customerInfo, hasPro) {
    try {
      const apiBase = (typeof process !== 'undefined' && process.env && process.env.REACT_APP_BACKEND_URL) || '';
      if (!apiBase) return;
      const userId =
        this._appUserId ||
        (typeof localStorage !== 'undefined' && localStorage.getItem('quranplus_user_id'));
      if (!userId || userId === 'anonymous') return;

      let entitlementId = null;
      let productId = null;
      let expiresAt = null;
      let originalPurchaseDate = null;
      let store = null;
      const active = customerInfo?.entitlements?.active || {};
      const desired = String(ENTITLEMENT_ID).toLowerCase();
      let ent = active[ENTITLEMENT_ID];
      if (!ent) {
        for (const key of Object.keys(active)) {
          if (key.toLowerCase() === desired) { ent = active[key]; entitlementId = key; break; }
        }
      } else {
        entitlementId = ENTITLEMENT_ID;
      }
      if (ent) {
        productId = ent.productIdentifier || null;
        expiresAt = ent.expirationDate || null;
        originalPurchaseDate = ent.originalPurchaseDate || null;
        store = ent.store || null;
      }

      await fetch(`${apiBase}/api/entitlements/sync-client`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: userId,
          is_pro: !!hasPro,
          entitlement_id: entitlementId,
          product_id: productId,
          expires_at: expiresAt,
          original_purchase_date: originalPurchaseDate,
          store,
        }),
      });
      console.log('[IAP] Backend entitlement sync sent — is_pro=', !!hasPro);
    } catch (e) {
      // Non-fatal — server-side webhook or subsequent restorePurchases()
      // will still reconcile eventually.
      console.warn('[IAP] Backend entitlement sync failed:', e?.message);
    }
  }

  /**
   * Get a specific product by ID
   */
  getProduct(productId) {
    return this.products.find(p => p.productId === productId);
  }

  /**
   * Get the current offerings
   */
  getOfferings() {
    return this.offerings;
  }

  /**
   * Purchase a subscription or lifetime access. No free trial is advertised
   * by the app — if the Play Store or App Store grants a trial via its own
   * offer configuration, RevenueCat reflects it in `entitlement.periodType`.
   *
   * Preferred signature: pass a selection object from the paywall, e.g.
   *   { plan: 'monthly', package: <RevenueCat package>, productId: '...' }
   * The RevenueCat package is used DIRECTLY - this guarantees the SKU sent to
   * Google Play matches exactly the plan the user tapped.
   *
   * Legacy signature: pass a string plan ('monthly' | 'yearly' | 'lifetime').
   * Falls back to resolving the package by subscriptionPeriod / productId
   * substring.
   *
   * @param {string|object} selection
   * @returns {Promise<{status: string, message: string, customerInfo?: object}>}
   */
  async purchasePro(selection = 'yearly') {
    await this.initialize();

    // Normalize selection into { plan, package, productId }
    let plan, explicitPackage, explicitProductId;
    if (typeof selection === 'string') {
      plan = selection;
      explicitPackage = null;
      explicitProductId = null;
    } else if (selection && typeof selection === 'object') {
      plan = selection.plan || 'yearly';
      explicitPackage = selection.package || null;
      explicitProductId = selection.productId || null;
    } else {
      plan = 'yearly';
    }

    try {
      if (isNativeApp() && this.revenueCat) {
        // Resolve the exact package to purchase.
        // Priority 1: explicit package passed from the paywall (the one the user tapped).
        // Priority 2: explicit productId passed from the paywall.
        // Priority 3: match by subscriptionPeriod for legacy string calls.
        let product = null;
        if (explicitPackage) {
          product = this.products.find(p => p.package === explicitPackage) || {
            productId: explicitProductId,
            package: explicitPackage,
          };
        } else if (explicitProductId) {
          product = this.products.find(p => p.productId === explicitProductId);
        } else {
          // Legacy string plan -> match by subscriptionPeriod (deterministic).
          product = this.products.find(p => {
            const period = (p.subscriptionPeriod || '').toUpperCase();
            if (plan === 'monthly') return period === 'P1M' || /^P\d+W$/.test(period);
            if (plan === 'yearly') return period === 'P1Y' || period === 'P12M';
            if (plan === 'lifetime') return !period;
            return false;
          });
          // Fallback: substring on full productId
          if (!product) {
            product = this.products.find(p => {
              const s = (p.productId || '').toLowerCase();
              if (plan === 'monthly') return s.includes('monthly') || s.includes('month');
              if (plan === 'yearly') return s.includes('yearly') || s.includes('annual') || s.includes('year');
              if (plan === 'lifetime') return s.includes('lifetime');
              return false;
            });
          }
        }
        
        console.log('[IAP] Purchasing plan:', plan, 'productId:', product?.productId);
        console.log('[IAP] Available products:', this.products.map(p => ({
          productId: p.productId,
          period: p.subscriptionPeriod,
        })));
        
        if (!product) {
          console.error('[IAP] Product not found for plan:', plan);
          return {
            status: PURCHASE_STATUS.ERROR,
            message: 'The selected plan is not available. Please try again or contact support.',
          };
        }
        
        if (!product.package) {
          console.error('[IAP] Product found but package is missing. Check RevenueCat Offerings configuration.');
          return {
            status: PURCHASE_STATUS.ERROR,
            message: 'Subscription setup incomplete. Please try again later.',
          };
        }
        
        // Initiate purchase - this launches the Play Store payment sheet
        // for the EXACT package the user selected. No silent SKU substitution.
        const { customerInfo: purchasedInfo } = await this.revenueCat.purchasePackage({
          aPackage: product.package,
        });
        
        // RevenueCat entitlements can propagate asynchronously - the customer
        // info returned inline from purchasePackage sometimes lacks the fresh
        // entitlement. Poll getCustomerInfo() for up to 8 seconds until the
        // entitlement appears, then fall back to whatever we have. This is the
        // "payment succeeded, app still locked" symptom users reported.
        let customerInfo = purchasedInfo;
        if (!customerInfo?.entitlements?.active?.[ENTITLEMENT_ID]) {
          const deadline = Date.now() + 8000;
          while (Date.now() < deadline) {
            try {
              const { customerInfo: refreshed } = await this.revenueCat.getCustomerInfo();
              if (refreshed?.entitlements?.active?.[ENTITLEMENT_ID]) {
                customerInfo = refreshed;
                break;
              }
            } catch (e) {
              console.warn('[IAP] getCustomerInfo poll error:', e?.message);
            }
            await new Promise(r => setTimeout(r, 500));
          }
        }
        
        this.customerInfo = customerInfo;
        
        // Log what entitlements we actually see, so misconfigured entitlement
        // ids surface clearly instead of silently failing.
        const activeIds = Object.keys(customerInfo?.entitlements?.active || {});
        console.log('[IAP] Active entitlements after purchase:', activeIds, 'looking for:', ENTITLEMENT_ID);
        
        // Check if pro entitlement is now active
        if (customerInfo.entitlements.active[ENTITLEMENT_ID]) {
          this.savePurchaseLocally(plan);

          const isLifetime = plan === 'lifetime';

          if (isLifetime) {
            return {
              status: PURCHASE_STATUS.SUCCESS,
              message: 'Welcome to Tanzeen+ Pro Lifetime! Enjoy all features forever.',
              customerInfo,
            };
          }

          // NOTE: RevenueCat exposes `entitlement.periodType === 'trial'` when
          // the Play Store / App Store granted a real free-trial offer. As of
          // the current Play Console configuration, neither monthly-base nor
          // yearly-base has a free-trial offer, so this will always be false.
          // Retained for forward-compatibility if a real store trial is added
          // later — the code path just uses the store's own trial signal
          // instead of a hard-coded 30-day claim.
          return {
            status: PURCHASE_STATUS.SUCCESS,
            message: `Welcome to Tanzeen+ Pro ${plan === 'monthly' ? 'Monthly' : 'Yearly'}!`,
            customerInfo,
          };
        }
        
        return { 
          status: PURCHASE_STATUS.CANCELLED, 
          message: 'Purchase was not completed.' 
        };
      } else {
        // WEB: Mock purchase for UI testing (no real transaction, no trial simulated).
        console.warn('[IAP] Web mode: mock success. Real purchases require the native app.');
        this.savePurchaseLocally(plan);
        return {
          status: PURCHASE_STATUS.SUCCESS,
          message: 'Web Preview: mock success. Real purchases require the iOS/Android app.',
        };
      }
    } catch (error) {
      console.error('[IAP] Purchase error:', error);
      
      if (error.code === 'PURCHASE_CANCELLED' || error.userCancelled) {
        return { status: PURCHASE_STATUS.CANCELLED, message: 'Purchase cancelled.' };
      }
      
      return { 
        status: PURCHASE_STATUS.ERROR, 
        message: error.message || 'Purchase failed. Please try again.' 
      };
    }
  }

  /**
   * Restore previous purchases (required by App Store and requested by user
   * on device switch / re-install).
   *
   * Concurrency: any second caller while a restore is already running
   * awaits the SAME underlying RevenueCat SDK call — no duplicate Play
   * Store round-trip, no duplicate backend sync, no duplicate toasts.
   *
   * @returns {Promise<{status: string, message: string, hasPro: boolean, error?: string}>}
   */
  async restorePurchases() {
    // In-flight coalescing. If a restore is already running, return the
    // same promise so double-tap / retry-loop callers all see one result.
    if (this._restoreInFlight) {
      console.log('[IAP] restorePurchases: coalescing concurrent call');
      return this._restoreInFlight;
    }

    const run = (async () => {
      await this.initialize();
      try {
        if (isNativeApp() && this.revenueCat) {
          // NATIVE: Restore from RevenueCat
          const customerInfo = await this.revenueCat.restorePurchases();
          this.customerInfo = customerInfo;

          const hasPro = this.hasActiveEntitlement(customerInfo);

          // Diagnostic — log what RevenueCat actually returned so misconfigured
          // dashboards (wrong entitlement id, inactive product) can be spotted
          // in device logs rather than failing silently.
          try {
            const active = customerInfo?.entitlements?.active || {};
            const activeKeys = Object.keys(active);
            console.log('[IAP] Restore result — active entitlements:', activeKeys,
                        'looking for:', ENTITLEMENT_ID, 'hasPro:', hasPro);
          } catch (_) {}

          // Push the fresh entitlement to the backend so server-side feature
          // gates immediately see the user as Pro (crucial when the backend
          // has no direct RevenueCat API key and mock-returns "free" by default).
          // Do NOT swallow this error into the "no purchases" path — a backend
          // sync failure is a separate risk we surface to the caller via
          // `syncError`, without blocking the restore itself.
          let syncError = null;
          try {
            await this.syncEntitlementToBackend(customerInfo, hasPro);
          } catch (e) {
            syncError = e?.message || 'backend_sync_failed';
            console.warn('[IAP] Backend entitlement sync failed after restore:', syncError);
          }

          // Notify observers (ProContext) so isPro / server verification refresh
          // even if we only detect the entitlement via case-insensitive fallback.
          (this._externalListeners || []).forEach((cb) => {
            try { cb(customerInfo, hasPro); } catch (e) {
              console.warn('[IAP] External listener error:', e?.message);
            }
          });

          if (hasPro) {
            this.savePurchaseLocally('restored');
            return {
              status: PURCHASE_STATUS.RESTORED,
              message: 'Your subscription has been restored!',
              hasPro: true,
              syncError,
            };
          }

          // No active Pro entitlement for the currently-bound RevenueCat
          // appUserID. Any lingering local Pro flag (e.g. from a previous
          // user on this device) MUST be cleared, otherwise checkProStatus
          // and checkLocalPurchase would keep returning true and grant User
          // B the previous owner's Pro.
          this.clearLocalPurchase();

          return {
            status: PURCHASE_STATUS.SUCCESS,
            message: 'No active subscriptions found on this account.',
            hasPro: false,
            syncError,
          };
        }

        // WEB: no RevenueCat SDK available. Check localStorage mock, but
        // only honour it if it belongs to the current appUserId. This is
        // what stops User A's Pro from persisting into User B's browser
        // session in the same install.
        const hasPro = this.checkLocalPurchase();
        return {
          status: hasPro ? PURCHASE_STATUS.RESTORED : PURCHASE_STATUS.SUCCESS,
          message: hasPro ? 'Pro restored!' : 'No purchases found.',
          hasPro,
        };
      } catch (error) {
        console.error('[IAP] Restore error:', error);
        // Distinguish network vs generic RevenueCat error so the UI can
        // show an actionable message rather than "No previous purchases".
        const msg = String(error?.message || error || '').toLowerCase();
        const isNetwork = msg.includes('network') || msg.includes('offline') || msg.includes('timeout');
        return {
          status: PURCHASE_STATUS.ERROR,
          message: isNetwork
            ? 'No internet connection. Please check your connection and try again.'
            : 'Failed to restore purchases. Please try again.',
          hasPro: false,
          error: error?.message || String(error),
        };
      }
    })();

    this._restoreInFlight = run;
    try {
      return await run;
    } finally {
      this._restoreInFlight = null;
    }
  }

  /**
   * Check if user has active Pro subscription
   * @returns {Promise<boolean>}
   */
  async checkProStatus() {
    // First check local cache
    if (this.checkLocalPurchase()) return true;

    if (isNativeApp() && this.revenueCat) {
      try {
        const customerInfo = await this.revenueCat.getCustomerInfo();
        this.customerInfo = customerInfo;
        
        const hasPro = this.hasActiveEntitlement(customerInfo);
        if (hasPro) this.savePurchaseLocally('subscription');
        return hasPro;
      } catch (e) {
        console.error('[IAP] Status check error:', e);
        return false;
      }
    }
    
    return false;
  }

  /**
   * Check if customer has active Pro entitlement.
   *
   * Strict check: we only grant Pro when the production entitlement id
   * (default: "Pro") is present in customerInfo.entitlements.active. There
   * is deliberately NO "any active entitlement grants Pro" fallback — that
   * would incorrectly grant access to holders of the "Tanzeen + Pro" TEST
   * entitlement configured in the RevenueCat dashboard.
   *
   * @param {object} customerInfo - RevenueCat customer info
   * @returns {boolean}
   */
  hasActiveEntitlement(customerInfo) {
    if (!customerInfo?.entitlements?.active) return false;

    // Case-insensitive lookup — the RevenueCat dashboard entitlement id
    // may be "Pro", "pro" or "PRO" depending on how the dashboard was
    // configured. Prior to 2026-09 this was a strict `[ENTITLEMENT_ID]`
    // lookup which failed silently when the case did not match, leaving
    // real subscribers stuck on the free tier after Restore Purchases.
    const active = customerInfo.entitlements.active;
    const desired = String(ENTITLEMENT_ID).toLowerCase();

    let entitlement = active[ENTITLEMENT_ID];
    if (!entitlement) {
      for (const key of Object.keys(active)) {
        if (key.toLowerCase() === desired) {
          entitlement = active[key];
          break;
        }
      }
    }

    if (!entitlement) return false;
    // Some SDK builds still surface expired entitlements in `active` briefly;
    // if the entitlement carries an explicit isActive flag, trust it.
    if (entitlement.isActive === false) return false;
    return true;
  }

  /**
   * Register an external observer for customer-info updates.
   * Called by ProContext to react to RevenueCat's async entitlement propagation
   * (webhook fires, cross-device sync, resume-from-Play-Store) so the UI can
   * flip isPro→true the instant the entitlement becomes active, without waiting
   * for the user to force-close and reopen the app.
   *
   * @param {(customerInfo: object, hasPro: boolean) => void} callback
   * @returns {() => void} unsubscribe function
   */
  onCustomerInfoUpdate(callback) {
    if (typeof callback !== 'function') return () => {};
    this._externalListeners = this._externalListeners || [];
    this._externalListeners.push(callback);
    // Fire immediately with whatever we already have, so ProContext sees the
    // current state without waiting for the next RevenueCat push.
    if (this.customerInfo) {
      try { callback(this.customerInfo, this.hasActiveEntitlement(this.customerInfo)); } catch (e) {
        console.warn('[IAP] External listener error:', e?.message);
      }
    }
    return () => {
      this._externalListeners = (this._externalListeners || []).filter(cb => cb !== callback);
    };
  }

  /**
   * Force a fresh getCustomerInfo() call and notify external listeners.
   * ProContext calls this on app-foreground so we detect subscriptions that
   * completed while the app was backgrounded (e.g. user just paid in Google Play).
   *
   * @returns {Promise<boolean>} true iff Pro is now active
   */
  async refreshCustomerInfo() {
    if (!isNativeApp() || !this.revenueCat) return this.checkLocalPurchase();
    try {
      const { customerInfo } = await this.revenueCat.getCustomerInfo();
      this.customerInfo = customerInfo;
      const hasPro = this.hasActiveEntitlement(customerInfo);
      if (hasPro) this.savePurchaseLocally('subscription');
      (this._externalListeners || []).forEach(cb => {
        try { cb(customerInfo, hasPro); } catch (e) {
          console.warn('[IAP] External listener error:', e?.message);
        }
      });
      return hasPro;
    } catch (e) {
      console.warn('[IAP] refreshCustomerInfo error:', e?.message);
      return false;
    }
  }

  /**
   * Get subscription details (expiration, type, etc.)
   * @returns {object|null}
   */
  getSubscriptionDetails() {
    if (!this.customerInfo) return null;
    
    const entitlement = this.customerInfo.entitlements?.active?.[ENTITLEMENT_ID];
    
    if (!entitlement) return null;
    
    return {
      isActive: true,
      productId: entitlement.productIdentifier,
      expiresDate: entitlement.expirationDate,
      isTrial: entitlement.periodType === 'trial',
      isLifetime: !entitlement.expirationDate, // Lifetime has no expiration
      willRenew: entitlement.willRenew,
      originalPurchaseDate: entitlement.originalPurchaseDate,
    };
  }

  /**
   * Login with user ID (for cross-device subscription sync).
   *
   * NOTE: in this app, `initialize(userId)` already binds the SDK to the
   * app's stable user id via `configure({appUserID})` + a follow-up `logIn`.
   * This method is kept for explicit re-binding (e.g. if the app ever
   * introduces true multi-account switching). Calling with the same id is a
   * no-op on the RevenueCat side.
   *
   * @param {string} userId - Your app's unique user ID
   */
  async loginUser(userId) {
    if (!this.revenueCat || !userId) return;
    if (this._appUserId === userId) return { customerInfo: this.customerInfo, isNewUser: false };

    try {
      const { customerInfo, created } = await this.revenueCat.logIn({ appUserID: userId });
      this.customerInfo = customerInfo;
      this._appUserId = userId;
      console.log(`[IAP] User ${created ? 'created' : 'logged in'}:`, userId);
      return { customerInfo, isNewUser: created };
    } catch (error) {
      console.error('[IAP] Login error:', error);
      throw error;
    }
  }

  /**
   * Logout user (resets SDK to anonymous). No public UI triggers this yet;
   * retained for symmetry.
   */
  async logoutUser() {
    if (!this.revenueCat) return;

    try {
      const customerInfo = await this.revenueCat.logOut();
      this.customerInfo = customerInfo;
      this._appUserId = null;
      this.clearLocalPurchase();
      console.log('[IAP] User logged out');
    } catch (error) {
      console.error('[IAP] Logout error:', error);
    }
  }

  // --- Local Storage Helpers (for web mock and caching) ---
  //
  // Every local cache entry is bound to the current appUserId. Reads that
  // find a mismatch treat the cache as absent (and purge it) so no
  // previous user's Pro can ever unlock the current session. Writes always
  // stamp the current appUserId — this is the guard for the
  // "shared/reset device" scenario tests exercise.

  _currentUserIdSync() {
    if (this._appUserId) return this._appUserId;
    try { return window?.localStorage?.getItem('quranplus_user_id') || null; } catch (_) { return null; }
  }

  savePurchaseLocally(plan) {
    try {
      const userId = this._currentUserIdSync();
      localStorage.setItem(LOCAL_SUB_KEY, JSON.stringify({
        plan,
        startDate: new Date().toISOString(),
        platform: getPlatform(),
        userId,
      }));
      if (userId) {
        try { localStorage.setItem(LOCAL_SUB_BOUND_USER_KEY, userId); } catch (_) {}
      }
    } catch (e) {
      console.error('[Purchase] Failed to save local purchase:', e.message);
    }
  }

  checkLocalPurchase() {
    try {
      const raw = localStorage.getItem(LOCAL_SUB_KEY);
      if (!raw) return false;
      const subscription = JSON.parse(raw);
      if (!subscription || !subscription.plan) return false;
      const boundUser = subscription.userId || null;
      const currentUser = this._currentUserIdSync();
      // If the entry has no userId stamp AND we have no current user, it
      // pre-dates the fix — treat as valid to avoid a false-negative on
      // first upgrade after update. This is safe because the server-side
      // verify_entitlement is still the source of truth for Pro.
      if (boundUser && currentUser && boundUser !== currentUser) {
        // Stale entry from a different user on this install. Clear it and
        // report no local Pro so User B can't inherit User A's Pro.
        try { localStorage.removeItem(LOCAL_SUB_KEY); } catch (_) {}
        return false;
      }
      return true;
    } catch (e) {
      console.debug('[Purchase] Failed to check local purchase:', e.message);
    }
    return false;
  }

  clearLocalPurchase() {
    localStorage.removeItem(LOCAL_SUB_KEY);
  }
}

export const purchaseService = new PurchaseService();
export { PurchaseService };
